'use client';

import React, { useState, useCallback } from 'react';
import { tv } from 'tailwind-variants';
import type {
  FormInstance,
  StepConfig,
  FormValues,
  FormErrors,
  FormTouched,
  FieldConfig,
  FieldGroup as FieldGroupType,
  AddMoreConfig,
  CustomErrorMessages,
} from '../../types';
import { InlineCard } from './InlineCard';
import { FieldGroup } from '../WillsFormStep/FieldGroup';
import ButtonWrapper from 'helpers/SitecoreWrappers/ButtonWrapper/ButtonWrapper';
import { useUIText } from '../../hooks/useUIText';
import { validateField, scrollToFirstError } from '../../utils/validation';

interface RadioInlineCardListProps {
  instances: FormInstance[];
  steps: StepConfig[];
  title: string;
  fieldGroup: FieldGroupType;
  values: FormValues;
  errors: FormErrors;
  touched: FormTouched;
  handleChange: (field: FieldConfig, rawValue: unknown) => void;
  setTouched: React.Dispatch<React.SetStateAction<FormTouched>>;
  setValues: React.Dispatch<React.SetStateAction<FormValues>>;
  setCompletedForms: React.Dispatch<React.SetStateAction<Record<string, FormInstance[]>>>;
  groupKey: string;
  fieldIds: string[]; // Field IDs from addMoreConfig
  displayTitleFields?: string[];
  displayFields?: string[];
  editableFields?: string[];
  editFields?: FieldConfig[];
  showEdit?: boolean;
  isSubmitting?: boolean;
  setErrors?: React.Dispatch<React.SetStateAction<FormErrors>>;
  showAddButton?: boolean; // Enable "Add new" button
  canAddMore?: boolean; // Respect maxInstances: hide Add when at limit (default true)
  instancePrefix?: string; // Prefix for generating instance IDs
  cardTitle?: string; // Title for the button text
  completedForms?: Record<string, FormInstance[]>; // For validation
  addMoreConfig?: AddMoreConfig; // For validation
  localStorageKey?: string; // For validation
  onNavigate?: (stepId: string) => void; // Navigation function
  addMoreStepId?: string; // Step ID to navigate to when adding new
  isDistributionStepWithNoBeneficiaries?: boolean;
  customErrorMessages?: CustomErrorMessages;
}

/**
 * Specialized InlineCardList for radio field inlineCards
 * Handles its own editing state and save functionality
 */
export const RadioInlineCardList: React.FC<RadioInlineCardListProps> = ({
  instances,
  steps,
  title,
  fieldGroup,
  values,
  errors,
  touched,
  handleChange,
  setTouched,
  setValues,
  setCompletedForms,
  groupKey,
  fieldIds,
  displayTitleFields,
  displayFields,
  editableFields,
  editFields,
  showEdit = true,
  isSubmitting = false,
  setErrors,
  showAddButton = false,
  canAddMore = false,
  cardTitle,
  completedForms,
  addMoreConfig,
  localStorageKey,
  onNavigate,
  addMoreStepId,
  isDistributionStepWithNoBeneficiaries = false,
  customErrorMessages,
}) => {
  const { getButtonText, getActionText } = useUIText();
  const [editingInstanceId, setEditingInstanceId] = useState<string | null>(null);
  const [originalInstanceData, setOriginalInstanceData] = useState<FormInstance | null>(null);

  // Create fieldGroup for editing (all fields, but non-editable ones are disabled)
  const getEditableFieldGroup = useCallback((): FieldGroupType | undefined => {
    // Include all fields from fieldGroup
    const fieldsToShow: FieldConfig[] = fieldGroup.fields.map((field) => {
      // Check if this field should be disabled (not in editableFields)
      const isEditable = editableFields
        ? editableFields.length === 0 || editableFields.includes(field.id)
        : true; // If no editableFields specified, all fields are editable

      // Return field with disabled property if not editable
      return {
        ...field,
        disabled: !isEditable,
      };
    });

    // Add editFields (new fields for editing - always editable)
    if (editFields && editFields.length > 0) {
      fieldsToShow.push(...editFields);
    }

    // Strip showIf so the inline edit form always renders when user clicks Edit.
    // The step's fieldGroup may have showIf (e.g. hasChildren === "Yes") which would hide
    // the form when we're on estate distribution where that field isn't in scope.
    const { showIf: _showIf, ...fieldGroupRest } = fieldGroup;
    return {
      ...fieldGroupRest,
      fields: fieldsToShow,
    };
  }, [fieldGroup, editableFields, editFields]);

  const editableFieldGroup = getEditableFieldGroup();

  // Handle edit click
  const handleEdit = useCallback(
    (instanceId: string) => {
      const instance = instances.find((inst) => inst.instanceId === instanceId);
      if (!instance) return;

      // Store original data for restoration if cancelled
      setOriginalInstanceData({ ...instance });

      // Set editing state
      setEditingInstanceId(instanceId);

      // Load instance data into form values
      const updatedValues = { ...values };

      // Load ALL instance data (including non-editable fields) so they show in the form (disabled)
      Object.keys(instance.data).forEach((fieldId) => {
        if (instance.data[fieldId] !== undefined) {
          updatedValues[fieldId] = instance.data[fieldId];
        }
      });

      if (
        groupKey === 'executor-group' &&
        values.executorChoice !== undefined &&
        instance.data.executorChoice === undefined
      ) {
        updatedValues.executorChoice = values.executorChoice;
      }

      if (
        groupKey === 'business-group' &&
        values.hasBusiness !== undefined &&
        instance.data.hasBusiness === undefined
      ) {
        updatedValues.hasBusiness = values.hasBusiness;
      }

      setValues(updatedValues);
      if (setErrors) {
        setErrors({}); // Clear errors when loading instance data
      }
    },
    [instances, values, setValues, setErrors, groupKey]
  );

  // Handle save
  const handleSave = useCallback(() => {
    if (!editingInstanceId) return;

    // Validate editable fields before saving
    if (editFields && setErrors) {
      const newErrors: Record<string, string | undefined> = {};
      let hasErrors = false;

      editFields.forEach((editField) => {
        const value = values[editField.id];
        const error = validateField(editField, value, {
          completedForms,
          allValues: values,
          editingInstanceId,
          addMoreConfig,
          localStorageKey,
          customErrorMessages,
        });
        if (error) {
          newErrors[editField.id] = error;
          hasErrors = true;
        }
      });

      if (hasErrors) {
        setErrors((prev) => ({ ...prev, ...newErrors }));
        // Mark fields as touched so errors display
        const newTouched: Record<string, boolean | undefined> = {};
        editFields.forEach((editField) => {
          if (newErrors[editField.id]) {
            newTouched[editField.id] = true;
          }
        });
        if (setTouched) {
          setTouched((prev) => ({ ...prev, ...newTouched }));
        }
        scrollToFirstError(newErrors);
        return; // Don't save if validation fails
      }
    }

    // Find the current instance to preserve all its existing data
    const currentInstance = instances.find((inst) => inst.instanceId === editingInstanceId);
    if (!currentInstance) return;

    // Start with existing instance data to preserve all fields (including from other cards with same groupKey)
    const updatedData: FormValues = { ...currentInstance.data };

    // Get editable fields or all fields
    const fieldsToSave = editableFields && editableFields.length > 0 ? editableFields : fieldIds;

    // Update only the fields we're editing
    fieldsToSave.forEach((fieldId) => {
      if (values[fieldId] !== undefined) {
        updatedData[fieldId] = values[fieldId];
      }
    });

    // Also include editFields values if any
    if (editFields) {
      editFields.forEach((editField) => {
        if (values[editField.id] !== undefined) {
          updatedData[editField.id] = values[editField.id];
        }
      });
    }

    if (fieldGroup && fieldGroup.fields) {
      fieldGroup.fields.forEach((field) => {
        if (values[field.id] !== undefined) {
          updatedData[field.id] = values[field.id];
        }
      });
    }

    Object.keys(currentInstance.data).forEach((fieldId) => {
      if (values[fieldId] !== undefined && values[fieldId] !== currentInstance.data[fieldId]) {
        updatedData[fieldId] = values[fieldId];
      }
    });
    if (
      (groupKey === 'executor-group' || groupKey === 'executor-group-choice') &&
      values.executorChoice !== undefined
    ) {
      updatedData.executorChoice = values.executorChoice;
    }

    if (groupKey === 'business-group' && values.hasBusiness !== undefined) {
      updatedData.hasBusiness = values.hasBusiness;
    }

    // Update the instance in completedForms
    setCompletedForms((prev) => {
      const currentInstances = prev[groupKey] || [];
      const updatedInstances = currentInstances.map((inst) =>
        inst.instanceId === editingInstanceId ? { ...inst, data: updatedData } : inst
      );
      return { ...prev, [groupKey]: updatedInstances };
    });

    // Clear editing state
    setEditingInstanceId(null);
    setOriginalInstanceData(null);

    // Clear form values for the edited fields
    const clearedValues = { ...values };
    fieldIds.forEach((fieldId) => {
      if (clearedValues[fieldId] !== undefined) {
        delete clearedValues[fieldId];
      }
    });
    if (editFields) {
      editFields.forEach((editField) => {
        if (clearedValues[editField.id] !== undefined) {
          delete clearedValues[editField.id];
        }
      });
    }
    setValues(clearedValues);
    setTouched({});
    if (setErrors) {
      setErrors({}); // Clear errors after save
    }
  }, [
    editingInstanceId,
    editFields,
    setErrors,
    instances,
    editableFields,
    fieldIds,
    fieldGroup,
    values,
    groupKey,
    setCompletedForms,
    setValues,
    setTouched,
    completedForms,
    addMoreConfig,
    localStorageKey,
  ]);

  // Handle cancel (restore original data)
  const handleCancel = useCallback(() => {
    if (!editingInstanceId || !originalInstanceData) return;

    // Restore original instance if it was removed
    setCompletedForms((prev) => {
      const currentInstances = prev[groupKey] || [];
      const instanceExists = currentInstances.some((inst) => inst.instanceId === editingInstanceId);
      if (!instanceExists) {
        return {
          ...prev,
          [groupKey]: [...currentInstances, originalInstanceData],
        };
      }
      return prev;
    });

    // Clear editing state
    setEditingInstanceId(null);
    setOriginalInstanceData(null);

    // Clear form values
    const clearedValues = { ...values };
    fieldIds.forEach((fieldId) => {
      if (clearedValues[fieldId] !== undefined) {
        delete clearedValues[fieldId];
      }
    });
    if (editFields) {
      editFields.forEach((editField) => {
        if (clearedValues[editField.id] !== undefined) {
          delete clearedValues[editField.id];
        }
      });
    }
    setValues(clearedValues);
    setTouched({});
    if (setErrors) {
      setErrors({}); // Clear errors after cancel
    }
  }, [
    editingInstanceId,
    originalInstanceData,
    groupKey,
    values,
    fieldIds,
    editFields,
    setCompletedForms,
    setValues,
    setTouched,
    setErrors,
  ]);

  // Helper function to get display fields for an instance (including editFields with data)
  const getInstanceDisplayFields = useCallback(
    (instance: FormInstance): string[] | undefined => {
      const fields: string[] = displayFields ? [...displayFields] : [];

      // Add editFields that have data in the instance
      if (editFields) {
        editFields.forEach((editField) => {
          if (
            instance.data[editField.id] !== undefined &&
            instance.data[editField.id] !== null &&
            instance.data[editField.id] !== ''
          ) {
            if (!fields.includes(editField.id)) {
              fields.push(editField.id);
            }
          }
        });
      }

      return fields.length > 0 ? fields : undefined;
    },
    [displayFields, editFields]
  );

  // Handle add new beneficiary - navigate to the beneficiary step form
  const handleAddNew = useCallback(() => {
    if (onNavigate && addMoreStepId) {
      onNavigate(addMoreStepId);
    }
  }, [onNavigate, addMoreStepId]);

  // Update early return condition to allow showing add button even when no instances
  if (instances.length === 0 && !showAddButton) {
    return null;
  }

  const { container, heading, editFormContainer, buttonsContainer, addButtonContainer } =
    TAILWIND_VARIANTS();
  return (
    <div className={container()}>
      {title && (instances.length > 0 || showAddButton) && (
        <h3 className={heading()}>
          {groupKey === 'spouse-partner-personal-details-group' ? 'Spouse/Partner' : cardTitle}
        </h3>
      )}
      {instances.map((instance, instanceIndex) => {
        // If this instance is being edited, render form instead of card
        if (editingInstanceId === instance.instanceId && editableFieldGroup) {
          return (
            <div key={instance.instanceId} className={editFormContainer()}>
              <FieldGroup
                fieldGroup={editableFieldGroup} // Use filtered fieldGroup
                values={values}
                errors={errors}
                touched={touched}
                handleChange={handleChange}
                setTouched={setTouched}
              />
              {/* Save and Cancel buttons */}
              <div className={buttonsContainer()}>
                <ButtonWrapper
                  type="button"
                  text={getButtonText('save', 'Save')}
                  ctaComponentClass="default"
                  ctaVariant="primary"
                  onClick={handleSave}
                  disabled={isSubmitting}
                  className="w-fit leading-[100%] md:leading-[100%] lg:leading-[100%] h-12"
                />
                <ButtonWrapper
                  type="button"
                  text={getButtonText('cancel', 'Cancel')}
                  ctaComponentClass="default"
                  ctaVariant="secondary"
                  onClick={handleCancel}
                  disabled={isSubmitting}
                  className="w-fit leading-[100%] md:leading-[100%] lg:leading-[100%] h-12"
                />
              </div>
            </div>
          );
        }

        // Otherwise, render the card normally
        // Include editFields in displayFields if they have data
        const instanceDisplayFields = getInstanceDisplayFields(instance);

        return (
          <InlineCard
            isRadioInlineCard={true}
            key={instance.instanceId}
            instance={instance}
            instanceIndex={instanceIndex}
            steps={steps}
            title={title}
            onEdit={showEdit ? handleEdit : () => {}}
            showEdit={showEdit}
            displayTitleFields={displayTitleFields}
            displayFields={instanceDisplayFields}
            editFields={editFields}
          />
        );
      })}

      {/* Add new beneficiary button - show after cards if showAddButton and under maxInstances */}
      {showAddButton && canAddMore && !isDistributionStepWithNoBeneficiaries && (
        <div className={addButtonContainer()}>
          <ButtonWrapper
            type="button"
            text={getActionText('addNewBeneficiary', `Add new ${cardTitle || 'beneficiary'}`, {
              cardTitle: cardTitle || 'beneficiary',
            })}
            ctaComponentClass="default"
            ctaVariant="primary"
            onClick={handleAddNew}
            disabled={isSubmitting}
            remixIcon="add-line"
            remixIconSize="sm"
            ctaIconAlignment="right"
            className="w-fit leading-[100%] md:leading-[100%] lg:leading-[100%] h-12"
          />
        </div>
      )}
    </div>
  );
};

const TAILWIND_VARIANTS = tv({
  slots: {
    container: ['w-full', 'flex', 'flex-col', 'justify-start', 'gap-spacing-spacing-24'],
    heading: [
      'font-typography-font-family-body',
      'text-typography-font-size-small',
      'leading-typography-line-height-small',
      'font-semibold',
      'text-component-section-body',
      'mt-spacing-spacing-24',
    ],
    editFormContainer: ['w-full', 'flex', 'flex-col', 'justify-start', 'gap-spacing-spacing-24'],
    buttonsContainer: ['w-fit', 'flex', 'flex-row', 'gap-spacing-spacing-16'],
    addButtonContainer: ['w-fit'],
  },
});
